import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { hikingPath, normalizeSoccerRedirect, accountPath } from '../utils/paths';
import { normalizeClubSport } from '../config/clubSports';
import {
  HIKER_EXPERIENCE_LEVELS,
  HIKER_GROUP_PREFERENCES,
  TRAIL_DIFFICULTIES,
  TRAIL_TERRAINS,
} from '../config/hikerProfileOptions';
import './PlayerRegistration.css';
import './HikingProfile.css';
import './AccountProfile.css';

const emptyForm = {
  experienceLevel: 'BEGINNER',
  preferredDifficulty: '',
  comfortableDistanceKm: '',
  preferredTerrains: [],
  groupPreference: '',
  canCarpool: false,
  emergencyContactName: '',
  emergencyContactPhone: '',
  medicalNotes: '',
};

const HikingProfileRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [account, setAccount] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [requestClubId, setRequestClubId] = useState('');
  const [hikingClubs, setHikingClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      const clubId = searchParams.get('clubId') || sessionStorage.getItem('votingClubId');
      const registerPath = clubId ? `/register?clubId=${encodeURIComponent(clubId)}` : '/register';
      navigate(registerPath, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  useEffect(() => {
    if (!user?.id) return;
    api.getAccountProfile(user.id).then(setAccount).catch(() => setAccount(null));
  }, [user?.id]);

  useEffect(() => {
    const preselectedClub = searchParams.get('clubId') || sessionStorage.getItem('votingClubId');
    if (preselectedClub) setRequestClubId(preselectedClub);
  }, [searchParams]);

  useEffect(() => {
    const loadClubs = async () => {
      try {
        const data = await api.getApprovedClubs();
        setHikingClubs(
          (Array.isArray(data) ? data : []).filter(
            (club) => normalizeClubSport(club.sport) === 'HIKING'
          )
        );
      } catch {
        setHikingClubs([]);
      }
    };
    loadClubs();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
    setSuccess('');
  };

  const toggleTerrain = (terrain) => {
    setFormData((prev) => {
      const selected = prev.preferredTerrains.includes(terrain)
        ? prev.preferredTerrains.filter((t) => t !== terrain)
        : [...prev.preferredTerrains, terrain];
      return { ...prev, preferredTerrains: selected };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        experienceLevel: formData.experienceLevel,
        preferredDifficulty: formData.preferredDifficulty || null,
        comfortableDistanceKm: formData.comfortableDistanceKm
          ? parseFloat(formData.comfortableDistanceKm)
          : null,
        preferredTerrains: formData.preferredTerrains,
        groupPreference: formData.groupPreference || null,
        canCarpool: formData.canCarpool,
        emergencyContactName: formData.emergencyContactName || null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
        medicalNotes: formData.medicalNotes || null,
      };
      const response = await api.saveHikerProfile(user.id, payload, { create: true });
      let successMessage = 'Hiking profile saved.';

      const clubIdToJoin = requestClubId || sessionStorage.getItem('votingClubId');
      const membershipPlayerId = response.membershipPlayerId;
      if (clubIdToJoin && membershipPlayerId) {
        try {
          await api.requestClubMembership(clubIdToJoin, membershipPlayerId);
          successMessage += ' Your request to join the hiking club was sent.';
        } catch (clubErr) {
          if (
            clubErr.message?.toLowerCase().includes('already') ||
            clubErr.message?.toLowerCase().includes('pending')
          ) {
            successMessage += ` ${clubErr.message}`;
          }
        }
      }

      setSuccess(successMessage);
      const redirectUrl = sessionStorage.getItem('votingRedirectUrl');
      setTimeout(() => {
        if (redirectUrl) {
          sessionStorage.removeItem('votingRedirectUrl');
          sessionStorage.removeItem('votingClubId');
          navigate(normalizeSoccerRedirect(redirectUrl), { replace: true });
        } else {
          navigate(hikingPath('/profile'), { replace: true });
        }
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save hiking profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card hiking-profile-page">
      <h2>Complete your hiking profile</h2>
      <p className="form-help" style={{ marginBottom: '1rem' }}>
        Add hiking-specific details below. Your name, photo, and contact info come from your{' '}
        <Link to={accountPath()}>account profile</Link> and apply across all sports.
      </p>

      {account && (
        <p className="account-profile-page__shared-note">
          Signed in as <strong>{account.name}</strong>
          {account.phoneNumber ? ` · ${account.phoneNumber}` : ''}.{' '}
          <Link to={accountPath()}>Update account details →</Link>
        </p>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="experienceLevel">Hiking experience *</label>
          <select
            id="experienceLevel"
            name="experienceLevel"
            value={formData.experienceLevel}
            onChange={handleChange}
            required
          >
            {HIKER_EXPERIENCE_LEVELS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="preferredDifficulty">Preferred difficulty (optional)</label>
          <select
            id="preferredDifficulty"
            name="preferredDifficulty"
            value={formData.preferredDifficulty}
            onChange={handleChange}
          >
            <option value="">— Select —</option>
            {TRAIL_DIFFICULTIES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="comfortableDistanceKm">Comfortable distance in one hike (km, optional)</label>
          <input
            type="number"
            id="comfortableDistanceKm"
            name="comfortableDistanceKm"
            min="1"
            max="80"
            step="0.5"
            value={formData.comfortableDistanceKm}
            onChange={handleChange}
            placeholder="e.g. 12"
          />
        </div>

        <div className="form-group">
          <label>Preferred terrain (optional)</label>
          <div className="hiking-profile-page__chips">
            {TRAIL_TERRAINS.map((opt) => {
              const selected = formData.preferredTerrains.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`hiking-profile-page__chip${selected ? ' is-selected' : ''}`}
                  onClick={() => toggleTerrain(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="groupPreference">Group hike preference (optional)</label>
          <select
            id="groupPreference"
            name="groupPreference"
            value={formData.groupPreference}
            onChange={handleChange}
          >
            <option value="">— Select —</option>
            {HIKER_GROUP_PREFERENCES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="canCarpool" className="hiking-profile-page__checkbox">
            <input
              type="checkbox"
              id="canCarpool"
              name="canCarpool"
              checked={formData.canCarpool}
              onChange={handleChange}
            />
            I can drive others to the trailhead
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="emergencyContactName">Emergency contact name (optional)</label>
          <input
            type="text"
            id="emergencyContactName"
            name="emergencyContactName"
            value={formData.emergencyContactName}
            onChange={handleChange}
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label htmlFor="emergencyContactPhone">Emergency contact phone (optional)</label>
          <input
            type="tel"
            id="emergencyContactPhone"
            name="emergencyContactPhone"
            value={formData.emergencyContactPhone}
            onChange={handleChange}
            maxLength={30}
          />
        </div>

        <div className="form-group">
          <label htmlFor="medicalNotes">Medical notes (optional, private)</label>
          <textarea
            id="medicalNotes"
            name="medicalNotes"
            value={formData.medicalNotes}
            onChange={handleChange}
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="form-group">
          <label htmlFor="requestClubId">Request to join a hiking club (optional)</label>
          <select
            id="requestClubId"
            value={requestClubId}
            onChange={(e) => setRequestClubId(e.target.value)}
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">— No club / I'll join later —</option>
            {hikingClubs
              .filter((club) => club.allowNewMembers !== false)
              .map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
          </select>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save hiking profile'}
        </button>
      </form>
    </div>
  );
};

export default HikingProfileRegistration;
